import React, { Fragment, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Col, Card } from "react-bootstrap";
import { getRequest } from "api/axiosClient";
import PropTypes from "prop-types";

import { useNavigate } from "react-router-dom";
import Alert from "components/molecules/Alert/Alert";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import errorToast from "helpers/errorToast";
import TaskDialog from "pages/shipper/Tasks/components/TaskDialog";
import Button from "components/atoms/Button/Button";

const TasksWidget = function ({ isLayoutLocked, paymentLocation, isPreview, widgetId, handleWidgetDialogOpen }) {
  const intl = useIntl();
  const navigate = useNavigate();
  const [loadingTasks, setloadingTasks] = useState(true);
  const [success, setSuccess] = useState(null);
  const [openTasks, setOpenTasks] = useState([]);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [showDelayedMessage, setShowDelayedMessage] = useState(false);
  const [errors, setErrors] = useState({});

  const { REACT_APP_CARGOBASE_BASE_URL } = import.meta.env;
  const delayedTimeoutMs = 20000;

  useEffect(() => {
    let isMounted = true;
    setTimeout(() => {
      if (isMounted) setShowDelayedMessage(true);
    }, delayedTimeoutMs);

    getRequest(`${REACT_APP_CARGOBASE_BASE_URL}/tasks?status=open`)
      .then((res) => {
        if (isMounted) {
          setSuccess(true);
          setOpenTasks(res.data.results.filter((task) => task.status === "open"));
          setloadingTasks(false);
        }
      })
      .catch((err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "dashboard.tasksWidget.couldNotRetrieveTasks",
            defaultMessage: "Could not retrieve tasks. Please try again later.",
          }),
        ),
      )
      .then(() => {
        if (!success && isMounted) {
          setErrors({
            ...errors,
            tasks: true,
          });
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleShowTaskDialog = (id) => {
    setShowTaskDialog(true);
    setTaskId(id);
  };

  if (loadingTasks) {
    return (
      <div className="overflow-hidden">
        <LoadingSkeleton
          header={intl.formatMessage({
            id: showDelayedMessage ? "dashboard.contentDelayedLoading" : "general.resolutionCenter",
            defaultMessage: showDelayedMessage ? "Content is taking a while to load" : "Center",
          })}
          rows={isPreview ? 2 : 6}
          className="pl-16 pr-16 pt-1"
        />
      </div>
    );
  }

  if (success)
    return (
      <>
        {taskId && (
          <TaskDialog
            isOpen={showTaskDialog}
            close={() => {
              setShowTaskDialog(false);
            }}
            taskId={taskId}
          />
        )}

        <Card.Body className="overflow-auto" style={{ maxHeight: isPreview ? "350px" : "" }}>
          <div
            className={`d-flex justify-content-${
              isLayoutLocked ? "center" : "between"
            } align-items-center mb-2 pb-2 br-5 bg-white rounded-bottom-left-0 rounded-bottom-right-0`}
          >
            <h5 className="mb-0 pl-1">
              <FormattedMessage id="general.resolutionCenter" defaultMessage="Resolution Center" />
              {paymentLocation && paymentLocation !== "All" && (
                <span className="text-secondary ml-2" style={{ fontSize: 12, fontWeight: "normal" }}>
                  ({paymentLocation})
                </span>
              )}
            </h5>
            {!isLayoutLocked && !isPreview && (
              <Button
                className="edit-btn"
                onClick={() => {
                  handleWidgetDialogOpen(widgetId);
                }}
              >
                <FormattedMessage id="general.edit" defaultMessage="Edit" />
              </Button>
            )}
          </div>
          {!openTasks.length && (
            <div className="h-100 d-flex justify-content-center align-items-center">
              <span
                className="text-secondary font-weight-semibold"
                style={{ transform: isPreview ? "" : "translateY(-30px)" }}
              >
                <FormattedMessage id="dashboard.taskWidget.noTasksFound" defaultMessage="You have no tasks" />
              </span>
            </div>
          )}
          <ul className="row flex-column list-widget-container">
            {openTasks.map(({ id, shipment_id, name, type }, i) => (
              <Fragment key={`${shipment_id}_${id}`}>
                <li
                  className={`col list-widget-item ${!isLayoutLocked || isPreview ? "previewing" : ""} ${
                    i === openTasks.length - 1 ? "" : "border-bottom border-grey"
                  }`}
                >
                  <div
                    aria-hidden
                    onClick={() => {
                      if (isLayoutLocked && !isPreview) {
                        handleShowTaskDialog(id);
                      }
                      if (type === "pending_approval_request") {
                        const path = `/shipment/${shipment_id}`;
                        navigate(path);
                      }
                    }}
                    className="d-flex row text-primary text-decoration-none pt-4 pb-4"
                  >
                    <Col className="d-flex flex-column align-items-start justify-content-start">
                      <span className="text-primary font-weight-bold d-block pb-1">{shipment_id}</span>
                      <p className="mb-0">{name}</p>
                    </Col>
                  </div>
                </li>
                <hr className="list-widget-line" />
              </Fragment>
            ))}
          </ul>
        </Card.Body>
      </>
    );

  return (
    <Alert className="mb-0" variant="danger">
      <FormattedMessage
        id="dashboard.couldNotRetrieveUpdates"
        defaultMessage="We could not retrieve your updates. Please try again."
      />
    </Alert>
  );
};

TasksWidget.propTypes = {
  isLayoutLocked: PropTypes.bool,
  paymentLocation: PropTypes.string,
  isPreview: PropTypes.bool,
  widgetId: PropTypes.number,
  handleWidgetDialogOpen: PropTypes.func,
};

TasksWidget.defaultProps = {
  paymentLocation: null,
  isLayoutLocked: false,
  isPreview: false,
  widgetId: null,
  handleWidgetDialogOpen: () => {},
};

export default TasksWidget;
